/**
 * Strategy functions for the business model strategy
 */

import { StrategyPhase } from '../baseStrategy';
import { 
  initPhaseHandler, 
  suggestionsPhaseHandler, 
  conversationPhaseHandler, 
  renderComponentHandler 
} from './handlers';

// Import strategies directly to avoid circular dependency
import locationStrategy from '../locationStrategy';
import skillsStrategy from '../skillsStrategy';
import titleStrategy from '../titleStrategy';
import companyStrategy from '../companyStrategy';
import educationStrategy from '../educationStrategy';
import experienceStrategy from '../experienceStrategy';
import defaultStrategy from '../defaultStrategy';

// Create a mapping of topic names to strategy functions
const strategyMap = {
  location: locationStrategy,
  skills: skillsStrategy,
  title: titleStrategy,
  company: companyStrategy,
  education: educationStrategy,
  experience: experienceStrategy,
  default: defaultStrategy
};

/**
 * Helper function to get the appropriate strategy based on topic and role type
 * @param {string} topic - The topic name
 * @param {string} roleType - The role type
 * @returns {Function} The strategy function
 */
export const getNextStrategy = (topic, roleType) => {
  console.log(`Getting strategy for topic: ${topic}, role type: ${roleType}`);
  
  if (!topic) {
    console.warn('No topic provided to getNextStrategy, using default strategy');
    return strategyMap.default;
  }
  
  const strategy = strategyMap[topic];
  if (!strategy) {
    console.warn(`No strategy found for topic "${topic}", using default strategy`);
    return strategyMap.default;
  }
  
  console.log(`Found strategy for topic "${topic}": ${strategy.name || 'unnamed strategy'}`);
  return strategy;
};

/**
 * Create a business model strategy that tracks its own state
 * This approach uses a closure to maintain state between calls
 */
export const createBusinessModelStrategy = () => {
  // Internal state tracking
  let hasExecutedInitPhase = false;
  let hasExecutedSuggestionsPhase = false;
  
  // The actual strategy function
  return async (decision, messages, setMessages) => {
    console.log('Business model strategy executing with state:', { 
      hasExecutedInitPhase, 
      hasExecutedSuggestionsPhase,
      incomingPhase: decision.strategyPhase
    });
    
    // Update thinking accordion with the current state
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nBusiness model strategy state: ${JSON.stringify({
                  hasExecutedInitPhase,
                  hasExecutedSuggestionsPhase,
                  incomingPhase: decision.strategyPhase
                })}`
              }
            : msg
        );
        return updatedMessages;
      });
    }
    
    // Check if this is a render component request
    if (decision.actionType === 'RENDER_COMPONENT') {
      console.log('Rendering component for business model strategy');
      return await renderComponentHandler(decision, messages, setMessages);
    }
    
    // If a specific phase is requested in the decision, honor it
    if (decision.strategyPhase === StrategyPhase.INIT) {
      console.log('Explicitly requested INIT phase');
      hasExecutedInitPhase = true;
      return await initPhaseHandler(decision, messages, setMessages);
    }
    else if (decision.strategyPhase === StrategyPhase.SUGGESTIONS) {
      console.log('Explicitly requested SUGGESTIONS phase');
      hasExecutedSuggestionsPhase = true;
      return await suggestionsPhaseHandler(decision, messages, setMessages);
    }
    else if (decision.strategyPhase === StrategyPhase.CONVERSATION) {
      console.log('Explicitly requested CONVERSATION phase');
      return await conversationPhaseHandler(decision, messages, setMessages);
    }
    // Otherwise, determine which phase to execute based on internal state
    else if (!hasExecutedInitPhase) {
      console.log('Executing init phase for first time');
      hasExecutedInitPhase = true;
      return await initPhaseHandler(decision, messages, setMessages);
    } 
    else if (!hasExecutedSuggestionsPhase) {
      console.log('Executing suggestions phase for first time');
      hasExecutedSuggestionsPhase = true;
      return await suggestionsPhaseHandler(decision, messages, setMessages);
    }
    else {
      console.log('Executing conversation phase');
      return await conversationPhaseHandler(decision, messages, setMessages);
    }
  };
};
